/**
 * @file        src/utils/inventory.js
 *              Inventory method and related helpers, originally written for Supervisor2.
 *
 * @author      Wes Garland, wes@kingsds.network
 * @date        Mar 2021
 */
'use strict';

const { EventEmitter } = require('events');
const inspect = Symbol.for('nodejs.util.inspect.custom');

/** 
 * Inventory constructor. This is very much like an Array, but methods `splice`, `fill`, `copyWithin`,
 * and direct access to elements is prohibited.
 *
 * Methods added to Inventory which are not on Array:
 * - add:       add an element to the Inventory
 * - delete:    remove an element from the Inventory
 * - replace:   replace one item in the inventory with another
 * - duplicate: return a new exports.Inventory, seeded with this Inventory's items
 *
 * Events 'add' and 'delete' are emitted whenever elements are added or deleted from
 * the inventory. Both 'add' and 'delete' are emitted during a replace operation.
 *
 * @constructor Inventory
 * @param {string}      name    [optional]              Name of the inventory; used for debugging
 * @param {object}      initialValues [optional]        An Array-like Object of initial values to add
 *                                                      to the Inventory without emitting the 'add' event.
 * @returns {object} instanceof Inventory
 */
exports.Inventory = function Inventory(name, initialValues, compare)
{
  var ee = new EventEmitter(name || 'Inventory');
  var px;

  if (initialValues)
  {
    if (!Array.isArray(initialValues))
      initialValues = Array.from(initialValues);
    else
      initialValues = [].concat(initialValues);
  }
  
  px = new Proxy([].concat(initialValues || []), {
    get: (target, prop, receiver) => {
      let promiscuous = false; /* see default case */

      if (typeof prop !== 'symbol' && !isNaN(+prop) && !promiscuous)
        throw new ReferenceError('indexed access is not allowed');

      const locate = (element) => {
        const idx = target.indexOf(element);
        if (idx === -1)
          throw new Error('invalid inventory item');
        return idx;
      }
      
      switch(prop)
      {
        case inspect:
        {
          return `[Object Inventory <${name || 'anonymous'}, ${target.length} items>]`;
        }
        case 'fill':
        case 'copyWithin':
        case 'splice':
        {
          throw new Error(`method ${prop} is not allowed`);
        }
        case 'delete':
        {
          return (element) => {
            const idx = locate(element);
            target.splice(idx, 1)[0];
            ee.emit('delete', element);
          }
        }
        case 'replace':
        {
          return (old, neu) => {
            const idx = locate(old);
            ee.emit('delete', target[idx]);
            target[idx] = neu;
            ee.emit('add', target[idx]);
          };
        }
        case 'pop':
        case 'shift':
        {
          return () => {
            let el = target[prop]();
            ee.emit('delete', el);
            return el;
          }
        }
        case 'add': {
          return (el) => {
            ee.emit('add', el);
            target.push(el);
            return el;
          }
        }
        case 'push':
        case 'unshift':
        {
          return (el) => {
            ee.emit('add', el);
            return target[prop](el);
          }
        }
        case 'on':
        case 'addListener':
        case 'addEventListener':
        case 'removeListener':
        case 'removeEventListener':
        case 'debug':
        case 'debugLabel':
        {
          if (typeof ee[prop] === 'function')
            return ee[prop].bind(ee);
          return ee[prop];
        }
        case 'duplicate':
        {
          return (__name, __compare) => new exports.Inventory(__name || name, target, __compare || compare);
        }
        case 'top':
        {
          return () => target[0];
        }
        case 'count':
        {
          return (arg) => target.filter(arg).length;
        }
        default:
        {
          if (typeof target[prop] !== 'function')
            return target[prop];

          /* Allow internal methods, eg. "push", to use numeric properties */
          return function Inventory$pxWrapper() {
            promiscuous = true;
            const ret = target[prop].apply(target, arguments)
            promiscuous = false;
            return ret;
          }
        }
      }
    },
    deleteProperty: function Inventory_deleteProperty()
    {
      return false;
    },
    defineProperty: function Inventory_defineProperty()
    {
      return false;
    },
    getPrototypeOf: function Inventory_getPrototypeOf(target)
    {
      return exports.Inventory;
    }
  });

  return px;
}
